import { Button } from '@/components/ui/button';
import { canViewContext } from '@/lib/auth/access';
import { config } from '@/lib/config';
import { useCurrentRole } from '@/lib/store/auth';
import { useUiStore } from '@/lib/store/ui';
import { Bug, Maximize2, Minimize2 } from 'lucide-react';
import { ContextToggles } from './ContextToggles';
import { MobileFilters } from './MobileFilters';
import { OrgSelector } from './OrgSelector';
import { ProfileMenu } from './ProfileMenu';
import { ViewMenu } from './ViewMenu';

export function Topbar() {
  const fullscreen = useUiStore((s) => s.fullscreenMap);
  const toggleFs = useUiStore((s) => s.toggleFullscreen);
  const debugMode = useUiStore((s) => s.debugMode);
  const toggleDebugMode = useUiStore((s) => s.toggleDebugMode);
  const ctx = useUiStore((s) => s.activeContext);
  const role = useCurrentRole();
  // ENG-280: no Debug button on a context the visitor can't open (login gate).
  const showDebug = !config.isMock && canViewContext(ctx, role);

  return (
    <header
      className="
        flex min-h-[52px] items-center gap-3 border-b border-(--color-border) bg-(--color-surface) px-3.5
        shrink-0 z-30 relative
      "
      style={{ boxShadow: '0 1px 4px rgba(0,0,0,.04)' }}
    >
      {/* Logo (mockup .logo-wrap, 26px tall <img> — the PNG already bakes in
          the "FLY[chevron]safe" wordmark; don't add separate text or it
          double-renders). */}
      <div className="flex items-center shrink-0 py-0.5">
        <img src="/flysafe-logo.png" alt="FLYsafe.live" className="h-[26px] block" />
      </div>
      <div className="h-7 w-px bg-(--color-border) shrink-0" />

      {/* Context-scoped toggle group. From `xl` up it sits inline; below that
          it collapses behind the Filters button (ENG-318). */}
      <div className="hidden xl:block flex-1 min-w-0 overflow-x-auto scrollbar-none">
        <ContextToggles />
      </div>
      <div className="xl:hidden flex-1 min-w-0">
        <MobileFilters />
      </div>

      {/* Right cluster (mockup .topbar-right) */}
      <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 ml-auto">
        <Button variant="outline" size="sm" onClick={toggleFs} className="hidden md:inline-flex">
          {fullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
          <span className="hidden lg:inline">Map</span>
        </Button>
        {/* Debug Mode is live-only — the raw broker stream isn't meaningful in mock. */}
        {showDebug && (
          <Button
            variant={debugMode ? 'default' : 'outline'}
            size="sm"
            onClick={toggleDebugMode}
            aria-pressed={debugMode}
            title="Debug Mode — inspect the raw MQTT for this tab's topics"
            className="hidden md:inline-flex"
          >
            <Bug size={13} />
            <span className="hidden lg:inline">Debug</span>
          </Button>
        )}
        <OrgSelector />
        <div className="hidden md:block h-7 w-px bg-(--color-border)" />
        <ViewMenu />
        <ProfileMenu />
      </div>
    </header>
  );
}
